import { useCallback, useMemo, useRef, useState } from "react";
import { GoogleMap, Polygon, Polyline, useJsApiLoader } from "@react-google-maps/api";
import * as turf from "@turf/turf";
import { toast } from "sonner";
import { Map as MapIcon, Ruler, Save, Trash2, Search, Eraser, KeyRound, Pencil, Check, Undo2, X } from "lucide-react";
import api from "@/lib/api";

const LIBS = ["geometry"];
const SQM_TO_SQFT = 10.7639;
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };

const LAYERS = {
  A: { label: "Gross Property Boundary", color: "#f59e0b", short: "Lot" },
  B: { label: "House Footprint & Roof", color: "#ef4444", short: "House" },
  C: { label: "Hardscapes", color: "#38bdf8", short: "Hardscape" },
};

const fmt = (n) => Math.round(n || 0).toLocaleString();

const toFeature = (path) => {
  if (!path || path.length < 3) return null;
  const ring = path.map((p) => [p.lng, p.lat]);
  ring.push(ring[0]);
  try { return turf.polygon([ring]); } catch { return null; }
};
const areaSqft = (feat) => { try { return turf.area(feat) * SQM_TO_SQFT; } catch { return 0; } };
const safeUnion = (feats) => {
  const list = feats.filter(Boolean);
  if (!list.length) return null;
  let acc = list[0];
  for (let i = 1; i < list.length; i++) {
    try { acc = turf.union(turf.featureCollection([acc, list[i]])) || acc; }
    catch { try { acc = turf.union(acc, list[i]) || acc; } catch { /* keep acc */ } }
  }
  return acc;
};
const safeDifference = (a, b) => {
  try { const d = turf.difference(turf.featureCollection([a, b])); return d || a; }
  catch { try { return turf.difference(a, b) || a; } catch { return a; } }
};

function SetupCard() {
  return (
    <div data-testid="turfmap-setup" className="rounded-2xl border border-amber-400/40 bg-amber-50 p-8 text-center max-w-xl mx-auto">
      <div className="w-12 h-12 rounded-2xl bg-amber-100 grid place-items-center mx-auto mb-3"><KeyRound className="w-5 h-5 text-amber-600" /></div>
      <h3 className="font-serif text-2xl tracking-tight">Connect Google Maps to enable Turf Map</h3>
      <p className="text-sm text-[var(--text-2)] mt-2">
        Add a Google Maps JavaScript API key (with <b>Maps JavaScript API</b> + <b>Geocoding API</b> enabled) as
        <code className="mx-1 px-1.5 py-0.5 rounded bg-white border border-amber-200 font-mono text-xs">REACT_APP_GOOGLE_MAPS_API_KEY</code>
        in the frontend environment, then restart the frontend. The satellite map, drawing tools and area math will appear here.
      </p>
    </div>
  );
}

function TurfCanvas({ apiKey, clients, reload }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: LIBS, id: "gmaps-turf" });
  const [clientId, setClientId] = useState("");
  const [activeLayer, setActiveLayer] = useState("A");
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [addr, setAddr] = useState("");
  const [saving, setSaving] = useState(false);
  const mapRef = useRef(null);

  const client = clients.find((c) => c.id === clientId) || null;

  const geocode = useCallback((address) => {
    if (!address || !window.google) return;
    const gc = new window.google.maps.Geocoder();
    gc.geocode({ address }, (res, status) => {
      if (status === "OK" && res[0] && mapRef.current) {
        mapRef.current.panTo(res[0].geometry.location);
        mapRef.current.setZoom(20);
      } else {
        toast.error("Couldn't locate that address — try a more specific one.");
      }
    });
  }, []);

  const onSelectClient = (id) => {
    setClientId(id);
    setShapes([]); setDraft([]); setDrawing(false);
    const c = clients.find((x) => x.id === id);
    if (c?.address) { setAddr(c.address); geocode(c.address); }
    else setAddr("");
  };

  const onMapClick = (e) => {
    if (!drawing || !e.latLng) return;
    setDraft((d) => [...d, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
  };
  const startDraw = () => { setDrawing(true); setDraft([]); };
  const undoPoint = () => setDraft((d) => d.slice(0, -1));
  const cancelDraw = () => { setDrawing(false); setDraft([]); };
  const finishDraw = () => {
    if (draft.length < 3) return toast.error("Add at least 3 corners");
    setShapes((s) => [...s, { id: Date.now() + Math.random(), layer: activeLayer, path: draft }]);
    setDrawing(false); setDraft([]);
    toast.success(`${LAYERS[activeLayer].short} area added`);
  };
  const removeShape = (id) => setShapes((s) => s.filter((x) => x.id !== id));

  const totals = useMemo(() => {
    const byLayer = { A: [], B: [], C: [] };
    shapes.forEach((s) => { const f = toFeature(s.path); if (f) byLayer[s.layer].push(f); });
    const unionA = safeUnion(byLayer.A);
    const unionB = safeUnion(byLayer.B);
    const unionC = safeUnion(byLayer.C);
    const lot = unionA ? areaSqft(unionA) : 0;
    const house = unionB ? areaSqft(unionB) : 0;
    const hardscape = unionC ? areaSqft(unionC) : 0;
    let grassFeat = unionA;
    [unionB, unionC].filter(Boolean).forEach((sub) => { if (grassFeat) grassFeat = safeDifference(grassFeat, sub); });
    const grass = grassFeat ? areaSqft(grassFeat) : 0;
    return { lot, house, hardscape, grass: Math.max(0, grass) };
  }, [shapes]);

  const save = async () => {
    if (!client) return toast.error("Pick a client first");
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}/measurement`, {
        lot_sqft: totals.lot, grass_sqft: totals.grass,
        house_sqft: totals.house, hardscape_sqft: totals.hardscape,
        shapes: shapes.map((s) => ({ layer: s.layer, path: s.path })),
      });
      toast.success(`Saved to ${client.name}'s profile`);
      reload && reload();
    } catch { toast.error("Could not save measurement"); } finally { setSaving(false); }
  };

  if (!isLoaded) return <div data-testid="turfmap-loading" className="h-[520px] grid place-items-center text-sm text-[var(--text-2)]">Loading satellite map…</div>;

  const saved = client?.property_measurement;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="turfmap">
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <select data-testid="turfmap-client" value={clientId} onChange={(e) => onSelectClient(e.target.value)}
            className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)] sm:w-64">
            <option value="">Select a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-2)]" />
            <input data-testid="turfmap-address" value={addr} onChange={(e) => setAddr(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") geocode(addr); }}
              placeholder="Search / type an address, press Enter"
              className="w-full h-11 pl-9 pr-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          </div>
          <button data-testid="turfmap-locate" onClick={() => geocode(addr)} className="h-11 px-4 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors shrink-0">Locate</button>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="turfmap-layers">
          <span className="label-mono text-[var(--text-2)]">Draw as</span>
          {Object.entries(LAYERS).map(([k, v]) => (
            <button key={k} data-testid={`turfmap-layer-${k}`} onClick={() => setActiveLayer(k)}
              className={`h-9 px-3 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${activeLayer === k ? "text-white" : "bg-white"}`}
              style={activeLayer === k ? { background: v.color, borderColor: v.color } : { borderColor: v.color, color: v.color }}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: v.color }} /> {k} · {v.label}
            </button>
          ))}
        </div>

        {/* Drawing toolbar */}
        <div className="flex flex-wrap items-center gap-2" data-testid="turfmap-draw-tools">
          {!drawing ? (
            <button data-testid="turfmap-draw-start" onClick={startDraw}
              className="h-9 px-4 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-colors"
              style={{ background: LAYERS[activeLayer].color }}>
              <Pencil className="w-4 h-4" /> Draw {LAYERS[activeLayer].short} area
            </button>
          ) : (
            <>
              <span className="text-xs text-[var(--text-2)] font-mono">Click the map to drop corners · {draft.length} point{draft.length === 1 ? "" : "s"}</span>
              <button data-testid="turfmap-draw-finish" onClick={finishDraw} disabled={draft.length < 3}
                className="h-9 px-3 rounded-full text-xs font-semibold bg-emerald-500 text-[#04120c] flex items-center gap-1.5 hover:bg-emerald-400 transition-colors disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Finish</button>
              <button data-testid="turfmap-draw-undo" onClick={undoPoint} disabled={!draft.length}
                className="h-9 px-3 rounded-full text-xs font-medium border border-[var(--line)] flex items-center gap-1.5 hover:border-[var(--accent)] transition-colors disabled:opacity-50"><Undo2 className="w-3.5 h-3.5" /> Undo</button>
              <button data-testid="turfmap-draw-cancel" onClick={cancelDraw}
                className="h-9 px-3 rounded-full text-xs font-medium border border-[var(--line)] flex items-center gap-1.5 hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><X className="w-3.5 h-3.5" /> Cancel</button>
            </>
          )}
        </div>

        <div className="border border-[var(--line)] overflow-hidden rounded-lg">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "520px" }}
            center={DEFAULT_CENTER}
            zoom={4}
            onLoad={(m) => { mapRef.current = m; }}
            onClick={onMapClick}
            options={{ mapTypeId: "satellite", tilt: 0, streetViewControl: false, fullscreenControl: true, mapTypeControl: true, draggableCursor: drawing ? "crosshair" : undefined }}
          >
            {shapes.map((s) => (
              <Polygon key={s.id} path={s.path}
                options={{ fillColor: LAYERS[s.layer].color, strokeColor: LAYERS[s.layer].color, fillOpacity: 0.35, strokeWeight: 2, clickable: false }} />
            ))}
            {draft.length > 0 && (
              <>
                <Polyline path={draft.length >= 3 ? [...draft, draft[0]] : draft} options={{ strokeColor: LAYERS[activeLayer].color, strokeOpacity: 0.95, strokeWeight: 2 }} />
                {draft.length >= 3 && <Polygon path={draft} options={{ fillColor: LAYERS[activeLayer].color, fillOpacity: 0.2, strokeOpacity: 0, clickable: false }} />}
              </>
            )}
          </GoogleMap>
        </div>
      </div>

      {/* Side panel */}
      <div className="space-y-3">
        <div className="rounded-2xl border border-emerald-500/25 bg-[#0a1310] text-slate-100 p-5" data-testid="turfmap-datacard">
          <h3 className="font-serif text-lg text-emerald-300 flex items-center gap-2"><Ruler className="w-4 h-4" /> Live Measurements</h3>
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-400">Total Lot Size</p>
              <p data-testid="turfmap-lot" className="font-mono text-3xl font-bold text-white">{fmt(totals.lot)}<span className="text-sm text-slate-400 ml-1">sq ft</span></p>
            </div>
            <div className="pt-3 border-t border-white/10">
              <p className="text-[11px] uppercase tracking-widest text-slate-400">Grass &amp; Island Area</p>
              <p data-testid="turfmap-grass" className="font-mono text-3xl font-bold text-emerald-300">{fmt(totals.grass)}<span className="text-sm text-slate-400 ml-1">sq ft</span></p>
              <p className="text-[11px] text-slate-500 mt-1">Lot minus house &amp; hardscapes</p>
            </div>
            <div className="pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-400">House</span><br /><span className="font-mono text-rose-300">{fmt(totals.house)} sq ft</span></div>
              <div><span className="text-slate-400">Hardscape</span><br /><span className="font-mono text-sky-300">{fmt(totals.hardscape)} sq ft</span></div>
            </div>
          </div>
          <button data-testid="turfmap-save" onClick={save} disabled={!client || saving}
            className="mt-4 w-full h-11 rounded-xl bg-emerald-500 text-[#04120c] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save to Client Profile"}
          </button>
          {saved && (
            <p className="text-[11px] text-slate-500 mt-2" data-testid="turfmap-saved-note">
              Last saved: {fmt(saved.lot_sqft)} sq ft lot · {fmt(saved.grass_sqft)} sq ft grass
            </p>
          )}
          {!client && <p className="text-[11px] text-amber-300/80 mt-2">Select a client to enable saving.</p>}
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="label-mono text-[var(--text-2)]">Shapes ({shapes.length})</p>
            {shapes.length > 0 && (
              <button data-testid="turfmap-clear" onClick={() => setShapes([])} className="text-xs text-[var(--danger)] hover:underline flex items-center gap-1"><Eraser className="w-3 h-3" /> Clear all</button>
            )}
          </div>
          {shapes.length === 0 ? (
            <p className="text-xs text-[var(--text-2)]">Pick a layer, tap <b>Draw area</b>, then click the map to trace corners and press Finish.</p>
          ) : (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto scroll-thin" data-testid="turfmap-shape-list">
              {shapes.map((s, i) => (
                <li key={s.id} data-testid="turfmap-shape-row" className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: LAYERS[s.layer].color }} />
                  <span className="flex-1 truncate">{LAYERS[s.layer].short} #{i + 1} · {fmt(areaSqft(toFeature(s.path)))} sq ft</span>
                  <button data-testid="turfmap-shape-remove" onClick={() => removeShape(s.id)} className="text-[var(--text-2)] hover:text-[var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TurfMap({ clients = [], reload }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  return (
    <div data-testid="turfmap-tab">
      <div className="flex items-center gap-2 mb-4">
        <MapIcon className="w-5 h-5 text-[var(--accent)]" />
        <h2 className="font-serif text-2xl tracking-tight">Turf Map · Property Measuring</h2>
      </div>
      {!apiKey ? <SetupCard /> : <TurfCanvas apiKey={apiKey} clients={clients} reload={reload} />}
    </div>
  );
}
